import { WeddingInvitation } from '../../types/invitation';
import { supabase } from './client';
import { DBWeddingInvitation } from './types';
import { transformDBInvitationToApp } from './transforms';
import { authService } from './authService';
import { createLogger } from '../logger';

const invitationLog = createLogger('invitation');

export const invitationService = {
  async createInvitation(invitation: Omit<WeddingInvitation, 'id' | 'createdAt'>, userId?: string): Promise<string> {
    const currentUser = await authService.getCurrentUser();
    const finalUserId = userId || currentUser?.id;

    if (!finalUserId) {
      throw new Error('必須登入才能建立邀請函');
    }

    const dbInvitation: Omit<DBWeddingInvitation, 'id' | 'created_at'> = {
      invite_code: invitation.inviteCode,
      bride_name: invitation.brideName,
      groom_name: invitation.groomName,
      wedding_date: invitation.weddingDate,
      wedding_time: invitation.weddingTime,
      venue: invitation.venue,
      address: invitation.address,
      has_welcome_party: invitation.hasWelcomeParty,
      has_wedding_ceremony: invitation.hasWeddingCeremony,
      has_reception: invitation.hasReception,
      show_transport_info: invitation.showTransportInfo,
      show_contact_info: invitation.showContactInfo,
      transport_details: invitation.transportDetails,
      welcome_party_venue: invitation.welcomePartyVenue,
      welcome_party_address: invitation.welcomePartyAddress,
      welcome_party_time: invitation.welcomePartyTime,
      ceremony_venue: invitation.ceremonyVenue,
      ceremony_address: invitation.ceremonyAddress,
      ceremony_time: invitation.ceremonyTime,
      map_url: invitation.mapUrl,
      reception_venue: invitation.receptionVenue,
      reception_address: invitation.receptionAddress,
      reception_time: invitation.receptionTime,
      template: invitation.template,
      cover_image: invitation.coverImage,
      welcome_message: invitation.welcomeMessage,
      primary_color: invitation.primaryColor,
      contact_phone: invitation.contactPhone,
      contact_email: invitation.contactEmail,
      is_active: invitation.isActive ?? true,
      accept_submissions: invitation.acceptSubmissions ?? true,
      user_id: finalUserId,
      footer_branding: invitation.footerBranding,
    };

    const { data, error } = await supabase
      .from('wedding_invitations')
      .insert(dbInvitation)
      .select('id')
      .single();

    if (error) throw error;
    return data.id;
  },

  async getInvitationByCode(inviteCode: string): Promise<WeddingInvitation | null> {
    invitationLog.debug('查詢邀請碼', inviteCode);

    const { data, error } = await supabase
      .from('wedding_invitations')
      .select('*')
      .eq('invite_code', inviteCode)
      .eq('is_active', true)
      .single();

    if (error) {
      invitationLog.error('查詢邀請碼錯誤:', error);
      if (error.code === 'PGRST116') {
        invitationLog.debug('找不到記錄');
        return null;
      }
      throw error;
    }

    invitationLog.info('成功找到資料', data);
    return transformDBInvitationToApp(data);
  },

  async updateInvitation(id: string, updates: Partial<WeddingInvitation>): Promise<void> {
    const dbUpdates: Partial<DBWeddingInvitation> = {};

    invitationLog.debug('準備更新邀請函', id, updates);

    if (updates.brideName) dbUpdates.bride_name = updates.brideName;
    if (updates.groomName) dbUpdates.groom_name = updates.groomName;
    if (updates.weddingDate) dbUpdates.wedding_date = updates.weddingDate;
    if (updates.weddingTime) dbUpdates.wedding_time = updates.weddingTime;
    if (updates.venue) dbUpdates.venue = updates.venue;
    if (updates.address) dbUpdates.address = updates.address;
    if (updates.hasWelcomeParty !== undefined) dbUpdates.has_welcome_party = updates.hasWelcomeParty;
    if (updates.hasWeddingCeremony !== undefined) dbUpdates.has_wedding_ceremony = updates.hasWeddingCeremony;
    if (updates.hasReception !== undefined) dbUpdates.has_reception = updates.hasReception;
    if (updates.showTransportInfo !== undefined) dbUpdates.show_transport_info = updates.showTransportInfo;
    if (updates.showContactInfo !== undefined) dbUpdates.show_contact_info = updates.showContactInfo;
    if (updates.transportDetails) dbUpdates.transport_details = updates.transportDetails;
    if (updates.welcomePartyVenue) dbUpdates.welcome_party_venue = updates.welcomePartyVenue;
    if (updates.welcomePartyAddress) dbUpdates.welcome_party_address = updates.welcomePartyAddress;
    if (updates.welcomePartyTime) dbUpdates.welcome_party_time = updates.welcomePartyTime;
    if (updates.ceremonyVenue) dbUpdates.ceremony_venue = updates.ceremonyVenue;
    if (updates.ceremonyAddress) dbUpdates.ceremony_address = updates.ceremonyAddress;
    if (updates.ceremonyTime) dbUpdates.ceremony_time = updates.ceremonyTime;
    if (updates.mapUrl) dbUpdates.map_url = updates.mapUrl;
    if (updates.receptionVenue) dbUpdates.reception_venue = updates.receptionVenue;
    if (updates.receptionAddress) dbUpdates.reception_address = updates.receptionAddress;
    if (updates.receptionTime) dbUpdates.reception_time = updates.receptionTime;
    if (updates.template) dbUpdates.template = updates.template;
    if (updates.coverImage) dbUpdates.cover_image = updates.coverImage;
    if (updates.welcomeMessage) dbUpdates.welcome_message = updates.welcomeMessage;
    if (updates.primaryColor) dbUpdates.primary_color = updates.primaryColor;
    if (updates.contactPhone) dbUpdates.contact_phone = updates.contactPhone;
    if (updates.contactEmail) dbUpdates.contact_email = updates.contactEmail;
    if (updates.isActive !== undefined) dbUpdates.is_active = updates.isActive;
    if (updates.acceptSubmissions !== undefined) dbUpdates.accept_submissions = updates.acceptSubmissions;
    if (updates.footerBranding !== undefined) dbUpdates.footer_branding = updates.footerBranding;

    invitationLog.debug('資料庫更新內容', dbUpdates);

    const { data, error } = await supabase
      .from('wedding_invitations')
      .update(dbUpdates)
      .eq('id', id)
      .select();

    if (error) throw error;

    if (!data || data.length === 0) {
      throw new Error('找不到要更新的邀請函，可能ID不正確');
    }

    invitationLog.info('更新成功', data);
  },

  async getAllInvitations(userId: string): Promise<WeddingInvitation[]> {
    try {
      const { data, error } = await supabase
        .rpc('get_user_invitations', { p_user_id: userId });

      if (error) {
        invitationLog.error('getAllInvitations RPC 錯誤:', error);
        const { data: fallbackData, error: fallbackError } = await supabase
          .from('wedding_invitations')
          .select('*')
          .eq('is_active', true)
          .eq('user_id', userId)
          .order('created_at', { ascending: false });

        if (fallbackError) throw fallbackError;
        return (fallbackData || []).map(invitation => ({
          ...transformDBInvitationToApp(invitation),
          role: 'owner' as const
        }));
      }

      return (data || []).map((item: any) => ({
        ...transformDBInvitationToApp(item),
        role: item.role as 'owner' | 'collaborator'
      }));
    } catch (error) {
      invitationLog.error('getAllInvitations 失敗:', error);
      return [];
    }
  },

  async deleteInvitation(id: string): Promise<void> {
    const { data, error } = await supabase
      .from('wedding_invitations')
      .update({ is_active: false })
      .eq('id', id)
      .select();

    if (error) throw error;

    if (!data || data.length === 0) {
      throw new Error('找不到要刪除的邀請函，可能已被刪除或ID不正確');
    }
  },

  async permanentDeleteInvitation(id: string): Promise<void> {
    const { error } = await supabase
      .from('wedding_invitations')
      .delete()
      .eq('id', id);

    if (error) throw error;
  },

  async toggleSubmissionStatus(id: string, acceptSubmissions: boolean): Promise<void> {
    invitationLog.debug('切換邀請函提交狀態', { id, acceptSubmissions });

    const { data, error } = await supabase
      .from('wedding_invitations')
      .update({ accept_submissions: acceptSubmissions })
      .eq('id', id)
      .select();

    if (error) {
      invitationLog.error('切換提交狀態失敗:', error);
      throw error;
    }

    if (!data || data.length === 0) {
      throw new Error('找不到要更新的邀請函，可能ID不正確');
    }

    invitationLog.info('提交狀態更新成功', {
      id,
      newStatus: acceptSubmissions ? '接受提交' : '停止接受提交'
    });
  },

  async getSubmissionStatus(inviteCode: string): Promise<boolean> {
    invitationLog.debug('查詢邀請函提交狀態', inviteCode);

    const { data, error } = await supabase
      .from('wedding_invitations')
      .select('accept_submissions')
      .eq('invite_code', inviteCode)
      .eq('is_active', true)
      .single();

    if (error) {
      invitationLog.error('查詢提交狀態失敗:', error);
      if (error.code === 'PGRST116') {
        throw new Error('邀請函不存在或已被刪除');
      }
      throw error;
    }

    return data.accept_submissions ?? true;
  }
};
